import { ClerkProvider, SignedIn, SignedOut, RedirectToSignIn, SignIn, SignUp } from '@clerk/clerk-react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from './contexts/AppContext';
import { ToastProvider } from './components/ui/Toast';
import AppLayout from './components/layouts/AppLayout';
import Dashboard from './components/features/dashboard/Dashboard';
import PlaylistPlanner from './components/features/playlist/PlaylistPlanner';
import PlaylistDeliveryNotifier from './components/features/playlist/PlaylistDeliveryNotifier';
import Settings from './components/features/settings/Settings';
import AgentList from './components/features/agents/AgentList';
import DownloadPage from './components/features/download/DownloadPage';
import PricingPage from './components/features/pricing/PricingPage';
import ThemeToggle from './components/shared/ThemeToggle';
import AuthLayout from './components/layouts/AuthLayout';
import { appClerkAppearance, authAppearance } from './config/authAppearance';
import { useDocumentDarkClass } from './hooks/useDocumentDarkClass';
import { useVersionCheck } from './hooks/useVersionCheck';

// Get Clerk publishable key from environment
const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!clerkPubKey) {
  throw new Error('Missing Clerk Publishable Key. Please set VITE_CLERK_PUBLISHABLE_KEY in your .env.local file.');
}

function App() {
  useVersionCheck();
  const isDark = useDocumentDarkClass();
  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      appearance={isDark ? appClerkAppearance.dark : appClerkAppearance.light}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
    >
      <BrowserRouter>
        <AppProvider>
          <ToastProvider>
            <ThemeToggle />
            <Routes>
              {/* Public routes */}
              <Route
                path="/sign-in/*"
                element={
                  <AuthLayout>
                    <SignIn
                      routing="path"
                      path="/sign-in"
                      signUpUrl="/sign-up"
                      appearance={authAppearance}
                    />
                  </AuthLayout>
                }
              />

              <Route
                path="/sign-up/*"
                element={
                  <AuthLayout>
                    <SignUp
                      routing="path"
                      path="/sign-up"
                      signInUrl="/sign-in"
                      appearance={authAppearance}
                    />
                  </AuthLayout>
                }
              />

              {/* Protected routes */}
              <Route
                path="/*"
                element={
                  <>
                    <SignedIn>
                      <AppLayout>
                        <PlaylistDeliveryNotifier />
                        <Routes>
                          <Route path="/" element={<Navigate to="/dashboard" replace />} />
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route path="/playlist" element={<PlaylistPlanner />} />
                          <Route path="/media-stations" element={<AgentList />} />
                          <Route path="/settings" element={<Settings />} />
                          <Route path="/download" element={<DownloadPage />} />
                          <Route path="/pricing" element={<PricingPage />} />
                          <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                      </AppLayout>
                    </SignedIn>
                    <SignedOut>
                      <RedirectToSignIn />
                    </SignedOut>
                  </>
                }
              />
            </Routes>
          </ToastProvider>
        </AppProvider>
      </BrowserRouter>
    </ClerkProvider>
  );
}

export default App;



